import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";

import {
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from "helpers/reducerHelpers";

const packingListUrl = "/crm/shipment/packing_lists";
const packingListFactoryUrl = "/crm/shipment/packing_list_factories"

export const getPackingLists = createAsyncThunk(
  "Shipment/getPackingLists",
  async (params) => {
    try {
      const response = await apiAxios.get(packingListUrl, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const createPackingList = createAsyncThunk(
  "Shipment/createPackingList",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(packingListUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const updatePackingList = createAsyncThunk(
  "Shipment/updatePackingList",
  async ({ packingListId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${packingListUrl}/${packingListId}`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response.data);
    }
  }
)

export const deletePackingList = createAsyncThunk(
  "Shipment/deletePackingList",
  async (packingListId) => {
    try {
      const response = await apiAxios.delete(`${packingListUrl}/${packingListId}`);
      return { 
        packingListId, 
        data: response.data 
      };
      
    } catch (error) {
      throw new Error(error.message)
    }
  }
)

export const exportData = createAsyncThunk(
  "Shipment/exportData",
  async (params) => {
    //remove page and per_page in object
    const { page, per_page, ...rest } = params
    console.log(rest, page, per_page)

    const response = await apiAxios.get(`${packingListUrl}/export`, {params, responseType: 'blob'})
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `packing-lists.xlsx`);
    document.body.appendChild(link);
    link.click();
  }
)

export const getFactories = createAsyncThunk(
  "Shipment/getFactories",
  async (params) => {
    try {
      const response = await apiAxios.get(packingListFactoryUrl, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getFactory = createAsyncThunk(
  "Shipment/getFactory",
  async (id) => {
    try {
      const response = await apiAxios.get(`${packingListFactoryUrl}/${id}`)
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const createFactory = createAsyncThunk(
  "Shipment/createFactory",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(packingListFactoryUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const getListJobs = createAsyncThunk(
  "Shipment/getListJobs",
  async (params) => {
    try {
      const response = await apiAxios.get(`${packingListUrl}/list_jobs`, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getListBuildings = createAsyncThunk(
  "Shipment/getListBuildings",
  async (params) => {
    try {
      const response = await apiAxios.get(`${packingListUrl}/list_buildings`, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getShipmentForJobs = createAsyncThunk(
  "Shipment/getShipmentForJobs",
  async (params) => {
    try {
      const url = "/crm/shipment/jobs";
      const response = await apiAxios.get(url, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const importPackingList = createAsyncThunk(
  "shipment/importPackingList",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(
        `${packingListUrl}/import`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Import API Error:', error);
      return rejectWithValue(error.response?.data || { message: 'Network error occurred' });
    }
  }
);

export const downloadTemplatePackingList = createAsyncThunk(
  "Shipment/downloadTemplatePackingList",
  async (templateType = 'packing-list', { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${packingListUrl}/download_template`, {
        params: { type: templateType },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${templateType}-template.xlsx`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Template downloaded successfully' };
    } catch (error) {
      console.error('Download template error:', error);
      return rejectWithValue(error.response?.data || { message: 'Download failed' });
    }
  }
);

const isRejectedAction = (action) => (action.type.startsWith("Shipment/") || action.type.startsWith("shipment/")) && action.type.endsWith("/rejected");

const packingListSlice = createSlice({
  name: "crm_shipment_packing_list",
  initialState: {
    ...initialState,
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackingLists.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))
      
      .addCase(createPackingList.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
      })
      .addCase(updatePackingList.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(deletePackingList.fulfilled, (state, action) => {

        const { error, packingListId } = action.payload;

        if (error !== undefined) {
          toast.error(error);
        } else {
          state.allData.data = state.allData.data.filter((item) => item.id !== packingListId)
          toast.success(`Packing list ${packingListId} was sucessfull deleted.`);
        }
      })
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Data has been exported successfully.")
      })
      .addCase(importPackingList.pending, (state) => {
        state.importing = true;
        state.importError = null;
        state.importResult = null;
      })
      .addCase(importPackingList.fulfilled, (state, action) => {
        state.importing = false;
        if (action.payload.success) {
          state.importResult = action.payload.data;
          state.importError = null;
        } else {
          state.importError = action.payload.message || 'Import failed';
        }
      })
      .addCase(importPackingList.rejected, (state, action) => {
        state.importing = false;
        state.importError = action.payload?.message || 'Import failed';
        console.error('Import rejected:', action.payload);
      })

      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = packingListSlice.actions

export default packingListSlice.reducer